import React from 'react';
import styled, { keyframes } from 'styled-components';
import codewaveLogo from '../assets/codewave-logo.png';

const LoadingSpinner = ({ 
  size = 'large', 
  text = 'Loading...', 
  overlay = true, 
  color = '#105090',
  backgroundColor = 'rgba(255, 255, 255, 0.9)',
  showLogo = true
}) => {
  const logoSize = size === 'small' ? '40px' : size === 'medium' ? '60px' : '80px';
  const textSize = size === 'small' ? '12px' : size === 'medium' ? '14px' : '16px';

  const SpinnerComponent = (
    <SpinnerContainer>
      {showLogo ? (
        <LogoSpinner>
          <LogoImage 
            src={codewaveLogo} 
            alt="Codewave Technologies" 
            style={{ width: logoSize, height: 'auto' }}
          />
        </LogoSpinner>
      ) : (
        <svg className="spinner" viewBox="0 0 50 50" style={{ width: logoSize, height: logoSize }}>
          <circle 
            className="path" 
            cx="25" 
            cy="25" 
            r="20" 
            fill="none" 
            strokeWidth="4"
            style={{ stroke: color }}
          ></circle>
        </svg>
      )}
      {text && <LoadingText style={{ fontSize: textSize, color }}>{text}</LoadingText>}
    </SpinnerContainer>
  );

  if (overlay) {
    return (
      <LoadingOverlay style={{ backgroundColor }}>
        {SpinnerComponent}
      </LoadingOverlay>
    );
  }

  return SpinnerComponent;
};

// Animation keyframes
const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const dash = keyframes`
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
`;

const strokeColor = keyframes`
  0% {
    stroke: #105090;
  }
  50% {
    stroke: #2090B0;
  }
  100% {
    stroke: #105090;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const logoPulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const logoFloat = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
`;

// Styled components
const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-in-out;

  .spinner {
    margin-bottom: 20px;
    animation: ${rotate} 2s linear infinite;
  }

  .path {
    stroke-linecap: round;
    animation: 
      ${dash} 1.5s ease-in-out infinite,
      ${strokeColor} 4s ease-in-out infinite;
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const LoadingText = styled.p`
  font-weight: 500;
  margin: 10px 0 0 0;
`;

const LogoSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const LogoImage = styled.img`
  animation: ${logoPulse} 2s ease-in-out infinite, ${logoFloat} 3s ease-in-out infinite;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

export default LoadingSpinner;
